import React from 'react';
import ChoicePanel from './ChoicePanel';
import {
  Choice,
  ChoiceItem,
  Choices,
  JsonObject,
  Preferences,
} from 'services/hitchit/types/Choices';
import { getKeyFromPreference } from 'services/hitchit/types/shared/mapChoice';

export type GenericChoicePanelProps = {
  keyPrefix?: string;
  choices?: Record<string, Choice<ChoiceItem>>;
  preferences: Preferences;
  savedPreferences: Preferences;
  jsonObject?: JsonObject;
  onChangeDealChoices: <CHOICE_TYPE>(choiceKey: string, choice: CHOICE_TYPE) => void;
  onReset: (choiceKey: string) => void;
  onClear: (choiceKey: string) => void;
  onCancel: (choiceKey: string) => void;
  onSave: () => void;
  levelNested?: number;
};

export const createChoicePanel = ({
  levelNested = 0,
  ...props
}: GenericChoicePanelProps): JSX.Element[] => {
  const {
    keyPrefix,
    choices,
    preferences,
    savedPreferences,
    onChangeDealChoices,
    onReset,
    onClear,
    onCancel,
    onSave,
    jsonObject,
  } = props;

  const choicePanels: JSX.Element[] = [];

  choices &&
    Object.entries(choices).map(([key, value]) => {
      const choiceId = keyPrefix ? `${keyPrefix}.${key}` : key;
      const currentPreference = preferences[choiceId];
      const dependencyChoicePanels = [];
      const prerequisiteChoicePanels = [];

      if (value.dependencies) {
        const preferenceKey = getKeyFromPreference(value.type, currentPreference);

        // add all the dependency choice panels to the dependencies..
        if (value.dependencies[preferenceKey]) {
          const deps = value.dependencies[preferenceKey];

          const dependencyChoices: Choices = {};
          const prerequisiteChoices: Choices = {};

          Object.entries(deps).map(([key, value]) => {
            if (value.isPrerequisite) {
              prerequisiteChoices[key] = value;
            } else {
              dependencyChoices[key] = value;
            }
          });

          dependencyChoicePanels.push(
            ...createChoicePanel({
              ...props,
              keyPrefix: `${choiceId}.${preferenceKey}`,
              choices: dependencyChoices,
              levelNested: levelNested + 1,
            })
          );
          prerequisiteChoicePanels.push(
            ...createChoicePanel({
              ...props,
              keyPrefix: `${choiceId}.${preferenceKey}`,
              choices: prerequisiteChoices,
              levelNested: levelNested + 1,
            })
          );
        }
      }

      choicePanels.push(
        <ChoicePanel
          jsonObject={jsonObject}
          key={choiceId}
          choiceId={choiceId}
          choice={value}
          preference={currentPreference}
          savedPreference={savedPreferences[choiceId]}
          onChangeDealChoices={onChangeDealChoices}
          onReset={onReset}
          onClear={onClear}
          onCancel={onCancel}
          onSave={onSave}
          prerequisiteChoicePanels={prerequisiteChoicePanels}
          dependencyChoicePanels={dependencyChoicePanels}
          levelNested={levelNested}
        />
      );
    });

  return choicePanels;
};
